import { stringify as qsStringify } from 'qs';
import { filterEmptyValues } from './util-functions';
import { CreateEventTicketDto } from '../models/dto';

export const changeLocation = ({
	url,
	fallback,
	timeout = 2000,
	openFallback = true,
}: {
	url: string;
	fallback?: string;
	timeout?: number;
	openFallback?: boolean;
}) => {
	if (typeof window === 'undefined' || !url) {
		return;
	}

	console.log('deeplink: location.href = ', url);
	console.log('fallback: ', fallback);

	window.location.href = url;

	if (openFallback && fallback && fallback !== url) {
		setTimeout(function () {
			window.location.href = fallback;
		}, timeout);
	}
};

export const windowOpen = ({
	url,
	fallback,
	target = '_blank',
	timeout = 2000,
	openFallback = true,
}: {
	url: string;
	fallback?: string;
	target?: '_self' | '_blank' | '_parent' | '_top';
	timeout?: number;
	openFallback?: boolean;
}) => {
	if (typeof window === 'undefined' || !url) {
		return;
	}

	console.log('deeplink: window.open = ', url);
	console.log('fallback: ', fallback);

	const newWindow = window.open(url, target);

	if (!newWindow && openFallback && fallback && fallback !== url) {
		setTimeout(function () {
			window.location.href = fallback;
		}, timeout);
	}
};

export const clickPhantomAnchor = ({
	url,
	fallback,
	download,
	target,
	timeout = 2000,
	openFallback = true,
}: {
	url: string;
	fallback?: string;
	download?: string;
	target?: '_self' | '_blank' | '_parent' | '_top';
	timeout?: number;
	openFallback?: boolean;
}) => {
	if (typeof window === 'undefined' || !url) {
		return;
	}

	console.log('deeplink: anchor.click = ', url);
	console.log('fallback: ', fallback);

	const encodedUri = encodeURI(url);

	const wrapperDiv = document.createElement('div');
	wrapperDiv.style.display = 'none';

	const link = document.createElement('a');
	link.setAttribute('href', encodedUri);

	if (download) {
		link.setAttribute('download', download);
	}

	if (target) {
		link.setAttribute('target', target);
	}

	wrapperDiv.appendChild(link);
	document.body.appendChild(wrapperDiv);

	link.click();
	wrapperDiv.remove();

	if (fallback && openFallback && fallback !== url) {
		setTimeout(function () {
			window.location.href = fallback;
		}, timeout);
	}
};

export const createPhantomIFrame = ({
	url,
	fallback,
	timeout = 1000,
	openFallback = true,
}: {
	url: string;
	fallback?: string;
	timeout?: number;
	openFallback?: boolean;
}): void => {
	if (typeof window === 'undefined' || !url) {
		return;
	}

	console.log('deeplink: iframe.load = ', url);
	console.log('fallback: ', fallback);

	const iframe = document.createElement('iframe');
	iframe.style.border = 'none';
	iframe.style.width = '1px';
	iframe.style.height = '1px';
	iframe.src = url;

	iframe.onload = function () {
		console.log('iframe loaded');
		window.location.href = url;
	};

	iframe.onerror = function () {
		console.log('iframe error');

		if (!openFallback || !fallback) return;

		window.location.href = fallback;
	};

	document.body.appendChild(iframe);

	console.log('append iframe');

	setTimeout(function () {
		console.log('remove iframe');
		document.body.removeChild(iframe);

		if (!openFallback || !fallback) return;

		clickPhantomAnchor({
			url: fallback,
			target: '_blank',
		});
	}, timeout);
};

export const getEmailLink = (email: string, props?: {
	subject?: string;
	body?: string;
}) => {
	if (typeof window === 'undefined') {
		return;
	}

	if (!email) {
		throw new Error('Email is required');
	}

	const query = qsStringify({
		subject: props?.subject,
		body: props?.body,
	});

	return `mailto:${email}${query ? `?${query}` : ''}`;
};

// Universal app links are implemented on mobile

export const commonQueryKeys = {
	promocode: 'pc',
	referralCode: 'aff',
	redirect: 'redirect',
} as const;

export const e2eTestQueryKeys = {
	isE2E: 'playwright',
	e2eToken: 'e2eToken',
} as const;
export const cardInfoQueryKeys = {
	CARD_NUMBER: 'CARD_NUMBER',
	CARD_EXP_MONTH: 'CARD_EXP_MONTH',
	CARD_EXP_YEAR: 'CARD_EXP_YEAR',
	CARD_CVC: 'CARD_CVC',
} as const;
export type CardInfoQueryKeysType = Record<keyof typeof cardInfoQueryKeys, string>;

export const eventQueryKeys = {
	checkout: 'checkout',
	waitlist: 'waitlist',
	preselectedTierId: 'preselectedTierId',
	preselectedTierQty: 'preselectedTierQty',
	ticketsJson: 'ticketsJson',
} as const;

export const makeEventPageLink = ({
	eventAlias,
	shortLocalInstance,
	subPath,
	isCheckout,
	isWaitlist,
	preselectedTierId,
	preselectedTierQty,
	isE2E,
	e2eToken,
	referralCode,
	promocode,
	cardInfo,
	tickets,
}: {
	eventAlias: string;
	shortLocalInstance?: string;
	subPath?: 'tickets';
	isCheckout?: boolean;
	isWaitlist?: boolean;
	preselectedTierId?: string;
	preselectedTierQty?: number;
	tickets?: CreateEventTicketDto[];
	referralCode?: string;
	promocode?: string;
	isE2E?: true;
	e2eToken?: string;
	cardInfo?: CardInfoQueryKeysType;
}) => {
	const params = qsStringify(
		filterEmptyValues({
			[eventQueryKeys.checkout]: isCheckout && !isWaitlist ? 'true' : undefined,
			[eventQueryKeys.waitlist]: isWaitlist ? 'true' : undefined,
			[eventQueryKeys.preselectedTierId]: preselectedTierId,
			[eventQueryKeys.preselectedTierQty]: preselectedTierQty,
			[eventQueryKeys.ticketsJson]: tickets?.length ? JSON.stringify(tickets) : undefined,

			[commonQueryKeys.promocode]: promocode,
			[commonQueryKeys.referralCode]: referralCode,

			[e2eTestQueryKeys.isE2E]: isE2E ? 'true' : undefined,
			[e2eTestQueryKeys.e2eToken]: e2eToken,
			...(cardInfo ?? {}),
		})
	);

	const parts = ['event', eventAlias, shortLocalInstance, subPath].filter(Boolean);

	return `${parts.join('/')}${params ? `?${params}` : ''}`;
};

export const makeDashboardEventPageLink = ({
	baseEventId,
	instance,
	prePath,
	subPath,
	type,
	created,
	tab,
}: {
	baseEventId: string;
	instance: Date | string;
	prePath?: 'edit' | 'copy';
	subPath?: 'retention-rate';
	tab?: 'attendees' | 'analytics' | 'waitlist' | 'embed-code';
	type?: 'OnlyThis' | 'ThisAndFollowing';
	created?: true;
}) => {
	const params = qsStringify({
		instance: instance instanceof Date ? instance.toISOString() : instance,
		created: created ? 'true' : undefined,
		type,
		tab,
	});

	const parts = ['events', prePath, baseEventId, subPath].filter(Boolean);

	return `${parts.join('/')}${params ? `?${params}` : ''}`;
};

export const membershipQueryKeys = {
	getMembership: 'getMembership',
	membershipTierId: 'membershipTierId',
} as const;

export const eventBundleQueryKeys = {
	getEventBundle: 'getEventBundle',
	eventBundleId: 'eventBundleId',
} as const ;

export const makeHostPageLink = ({
	userName,
	tab,
	getMembership,
	membershipTierId,
	getEventBundle,
	eventBundleId,
	promocode,
	isE2E,
	e2eToken,
	cardInfo,
}: {
	userName: string;
	tab?: 'events' | 'membership' | 'perks' | 'calendar';
	getMembership?: true;
	membershipTierId?: string;
	getEventBundle?: true;
	eventBundleId?: string;
	promocode?: string;
	isE2E?: true;
	e2eToken?: string;
	cardInfo?: CardInfoQueryKeysType;
}) => {
	const params = qsStringify(
		filterEmptyValues({
			[membershipQueryKeys.getMembership]: getMembership ? 'true' : undefined,
			[membershipQueryKeys.membershipTierId]: membershipTierId,

			[eventBundleQueryKeys.getEventBundle]: getEventBundle ? 'true' : undefined,
			[eventBundleQueryKeys.eventBundleId]: eventBundleId,

			[commonQueryKeys.promocode]: promocode,

			[e2eTestQueryKeys.isE2E]: isE2E ? 'true' : undefined,
			[e2eTestQueryKeys.e2eToken]: e2eToken,
			...(cardInfo ?? {}),
		})
	);

	const doNotOpenMobile = getMembership || getEventBundle;

	// If doNotOpenMobile is true, we prepare the link by removing prefix - this disables the universal app link to mobile
	const parts = [doNotOpenMobile ? undefined : 'host', userName, tab].filter(Boolean);

	return `${parts.join('/')}${params ? `?${params}` : ''}`;
};

export const makeChallengePageLink = ({
	challengeAlias,
	promocode,
	isE2E,
	e2eToken,
	cardInfo,
}: {
	challengeAlias: string;
	promocode?: string;
	isE2E?: true;
	e2eToken?: string;
	cardInfo?: CardInfoQueryKeysType;
}) => {
	const params = qsStringify(
		filterEmptyValues({
			[commonQueryKeys.promocode]: promocode,

			[e2eTestQueryKeys.isE2E]: isE2E ? 'true' : undefined,
			[e2eTestQueryKeys.e2eToken]: e2eToken,
			...(cardInfo ?? {}),
		})
	);

	const parts = ['challenge', challengeAlias].filter(Boolean);

	return `${parts.join('/')}${params ? `?${params}` : ''}`;
};

export const makeOrderPageLink = ({
	orderId,
	subPath,
	ticketId,
}: {
	orderId: string;
	subPath?: 'transfer-ticket';
	ticketId?: string;
}) => {
	const params = qsStringify(
		filterEmptyValues({
			ticketId,
		})
	);

	const parts = ['order', orderId, subPath].filter(Boolean);

	return `${parts.join('/')}${params ? `?${params}` : ''}`;
};

export const makeTicketPageLink = ({
	ticketId,
	subPath,
}: {
	ticketId: string;
	subPath?: 'intake-form' | 'classes';
}) => {
	const parts = ['ticket', ticketId, subPath].filter(Boolean);

	return `${parts.join('/')}`;
};
